import React from "react";
import { Link2, HeartHandshake, Sparkles, Accessibility, Globe2, TrendingUp, GraduationCap } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/site/Reveal";

const IMG = {
  founder: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/l0t2ljgb_IMG_20260513_162825459~3%20%281%29.jpg",
  instructor: "https://customer-assets-eiarnc6j.emergentagent.net/job_alma-lengua-preview/artifacts/mfvv5syc_Keivan.png",
};
const valueIcons = [Link2, HeartHandshake, Accessibility, Globe2, TrendingUp];

export default function About() {
  const { tr } = useLang();
  const a = tr.about;

  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-aqua/50">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <Kicker>{tr.nav.about}</Kicker>
            <h1 className="font-heading font-extrabold text-navy text-4xl md:text-6xl tracking-tight leading-[1.05] mb-6">{a.heroTitle}</h1>
            <p className="text-lg md:text-xl text-ink/70 leading-relaxed max-w-2xl mx-auto">{a.heroSub}</p>
          </Reveal>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-5">
            <div className="relative max-w-sm lg:sticky lg:top-28">
              <div className="arch overflow-hidden shadow-[0_30px_80px_rgba(7,20,63,0.15)] aspect-[4/5] bg-aqua">
                <img src={IMG.founder} alt="La Profesora Alma, founder of Alma y Lengua Bilingual Learning Center" style={{ objectPosition: "center 15%" }} className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <Kicker>{a.founderKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-3xl md:text-5xl tracking-tight mb-8">{a.founderTitle}</h2>
            <div className="space-y-5 text-lg text-ink/70 leading-relaxed">
              {a.founderParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <p className="mt-8 font-accent italic text-xl md:text-2xl text-navy leading-relaxed border-l-4 border-gold pl-6">{a.founderClose}</p>
          </Reveal>
        </div>
      </section>

      {/* INSTRUCTOR */}
      <section className="py-24 lg:py-32 bg-navy text-white grain">
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <Kicker dark>{a.instructorKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-3xl md:text-5xl tracking-tight mb-8">{a.instructorTitle}</h2>
            <div className="space-y-5 text-lg text-white/70 leading-relaxed">
              {a.instructorParas.map((p, i) => <p key={i}>{p}</p>)}
            </div>
            <div className="mt-8 bg-white/5 rounded-3xl p-8 border border-white/10">
              <p className="flex items-center gap-3 text-gold font-semibold mb-4"><GraduationCap size={20} /> {a.instructorDegreesIntro}</p>
              <ul className="space-y-2 text-white/80">
                {a.instructorDegrees.map((d, i) => (
                  <li key={i} className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-gold" />{d}</li>
                ))}
              </ul>
            </div>
            <p className="mt-8 text-white/70 text-lg leading-relaxed">{a.instructorClose}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative max-w-sm lg:sticky lg:top-28">
              <div className="arch overflow-hidden shadow-2xl aspect-[4/5] bg-white/10">
                <img src={IMG.instructor} alt="Keivan Raika, instructor at Alma y Lengua Bilingual Learning Center" style={{ objectPosition: "center 25%" }} className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 text-center">
          <Reveal>
            <Kicker>{a.missionKicker}</Kicker>
            <p className="font-heading font-extrabold text-navy text-3xl md:text-5xl tracking-tight leading-[1.25]">{a.missionBody}</p>
          </Reveal>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 lg:py-32 bg-aqua/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl mb-14">
            <Kicker>{a.valuesKicker}</Kicker>
            <h2 className="font-heading font-extrabold text-navy text-4xl md:text-5xl tracking-tight">{a.valuesTitle}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {a.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={i} delay={i * 0.06}>
                  <div data-testid={`value-card-${i}`} className="bg-white rounded-3xl p-8 border border-navy/5 h-full hover:-translate-y-1 transition-transform">
                    <div className="w-14 h-14 rounded-2xl bg-navy text-gold grid place-items-center mb-6"><Icon size={26} /></div>
                    <h3 className="font-heading font-bold text-xl text-navy mb-3">{v.title}</h3>
                    <p className="text-ink/65 leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
